<template>
  <v-container fluid class="modulvalidasi-page pa-4">
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="6000">
      {{ snackbar.message }}
    </v-snackbar>
    <v-card class="mb-4" outlined>
      <v-card-title class="py-3 d-flex align-start kad__tajuk">
        <v-btn icon class="mr-2" :to="{ name: 'Modulvalidasi' }"><v-icon color="#037769">mdi-arrow-left</v-icon></v-btn>
        <!-- Title and its blurb are ONE column. As a sibling v-card-subtitle the
             blurb sat below the whole title row, so the tall legend on the right
             pushed it to the bottom of the card and left a gap under the
             heading. Nested here it stays where it belongs, whatever the legend
             does to the row's height. -->
        <div class="header-left">
          <div class="header-left__title subtitle-1 font-weight-medium primary--text" style="color: #037769 !important;">
            <v-icon left color="#037769">mdi-book-open-variant</v-icon>Rujukan Kriteria Validasi
          </div>
          <div class="header-left__sub">
            Senarai penuh kuiri Buku Panduan Validasi HIES/BA 2024/2025 - {{ counts.total }} kuiri rasmi.
            Lajur <b>Semakan (Panduan)</b> ialah petikan Buku Panduan; lajur <b>Logik Enjin</b>
            ialah syarat sebenar yang dinilai oleh enjin, supaya kedua-duanya boleh dibandingkan.
          </div>
        </div>
        <v-spacer></v-spacer>
        <!-- Counts, and directly under them the legend for the Status Enjin
             column - the chips there are the same three the column uses, so the
             badges and their explanation read as one block. -->
        <div class="header-right">
          <div class="header-right__chips">
            <v-btn small color="primary" dark class="ma-1" :loading="loading" @click="load" style="position: fixed; top: 80px; right: 20px; z-index: 999999;">
              <v-icon left small>mdi-refresh</v-icon>Muat Semula JS
            </v-btn>
            <v-btn small color="success" dark class="ma-1" @click="muatTurunExcel">
              <v-icon left small>mdi-file-excel</v-icon>Muat Turun Excel
            </v-btn>
            <v-chip class="ma-1" color="green darken-1" dark label small>Dalam Enjin: {{ counts.inEngine }}</v-chip>
            <v-chip v-if="counts.closed" class="ma-1" color="red darken-1" dark label small>Ditutup: {{ counts.closed }}</v-chip>
            <v-chip class="ma-1" color="grey darken-1" dark label small>Jumlah: {{ counts.total }}</v-chip>
          </div>
          <div class="status-legend">
            <div class="status-legend__head">
              <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
              <b>Status Enjin</b> - sejauh mana setiap kuiri Buku Panduan dilaksanakan dalam enjin Modul Validasi.
            </div>
            <div v-for="s in statusLegend" :key="s.label" class="status-legend__row">
              <v-chip :color="s.color" :dark="s.dark" x-small label class="status-legend__chip">{{ s.label }}</v-chip>
              <span>{{ s.text }}</span>
            </div>
          </div>
        </div>
      </v-card-title>
      <v-card-text class="filter-bar py-3">
        <!-- Buku Panduan filters -->
        <v-row align="end" dense>
          <v-col cols="12" sm="2">
            <div class="fld__label">Jenis Kuiri</div>
            <v-select v-model="familyFilter" :items="familyOptions" outlined dense hide-details clearable></v-select>
          </v-col>
          <v-col cols="12" sm="2">
            <div class="fld__label">Jenis Rekod</div>
            <v-select v-model="jenisFilter" :items="jenisOptions" outlined dense hide-details clearable></v-select>
          </v-col>
          <v-col cols="12" sm="2">
            <div class="fld__label">Status Enjin</div>
            <v-select v-model="statusFilter" :items="panduanStatusOptions" outlined dense hide-details clearable></v-select>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="fld__label">Cari</div>
            <v-text-field v-model="search" append-icon="mdi-magnify" outlined dense hide-details clearable></v-text-field>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="12" sm="auto">
            <v-btn outlined height="40" class="text-none reset-btn" @click="resetFilters">
              <v-icon small left>mdi-refresh</v-icon>
              Tetapkan Semula
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card outlined>
      <!-- ============ Buku Panduan table ============ -->
      <v-data-table
        :headers="panduanHeaders"
        :items="filteredKuiri"
        :search="search"
        :loading="loading"
        :items-per-page="50"
        dense
        class="kriteria-table"
        :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
      >
        <!-- Kod cell is coloured by the kuiri's severity (Kritikal / Amaran /
             Info). Hover names the tier. Severity is per-code, served from the
             catalogue, so this badge always matches what the engine emits. -->
        <template v-slot:[`item.code`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-chip v-bind="attrs" v-on="on" :color="sevColor(item.severity)" dark x-small label class="kod-chip">{{ item.code }}</v-chip>
            </template>
            <span>Keterukan: {{ item.severityLabel || sevLabel(item.severity) }}</span>
          </v-tooltip>
        </template>
        <template v-slot:[`item.family`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-chip v-bind="attrs" v-on="on" color="blue-grey lighten-4" x-small label>{{ item.family }}</v-chip>
            </template>
            <span>{{ families[item.family] || item.family }}</span>
          </v-tooltip>
        </template>
        <template v-slot:[`item.vars`]="{ item }">
          <v-chip v-for="c in item.vars" :key="c" class="ma-1" x-small outlined>{{ c }}</v-chip>
        </template>
        <template v-slot:[`item.semakan`]="{ item }">
          {{ item.semakan }}
          <div v-if="item.formula" class="grey--text text-caption">Σ {{ item.formula }}</div>
          <div v-if="item.lampiran" class="grey--text text-caption">Lampiran: {{ item.lampiran }}</div>
        </template>
        <!-- Apa yang enjin BENAR-BENAR nilai. Lajur Semakan hanya memetik niat
             Buku Panduan, yang tidak cukup untuk mengesahkan pelaksanaan; lajur
             ini menamakan jadual, lajur dan syarat cetusan sebenar. -->
        <template v-slot:[`item.logik`]="{ item }">
          <span v-if="item.logik" class="logik" :class="{ 'logik--off': !item.active }">{{ item.logik }}</span>
          <span v-else class="grey--text">-</span>
        </template>
        <!-- Chip and the Status Enjin filter both read kuiriStatus(item) - one
             source of truth so they can never disagree. Ulangan and Nyahaktif are
             BOTH skip states; they are told apart by the reason (Ulangan = the
             check runs under another code; Nyahaktif = it is not evaluated). -->
        <template v-slot:[`item.status`]="{ item }">
          <div class="status-cell-wrap">
            <!-- Tutup: closed manually; Semak Semula ignores it. Only reversible
                 back to Aktif. -->
            <template v-if="kuiriStatus(item) === 'closed'">
              <v-tooltip bottom max-width="420">
                <template v-slot:activator="{ on, attrs }">
                  <v-chip v-bind="attrs" v-on="on" color="red darken-1" dark x-small label>Tutup</v-chip>
                </template>
                <span>Kuiri ditutup - Semak Semula mengabaikannya. Klik butang untuk membuka semula kepada Aktif.</span>
              </v-tooltip>
              <v-btn icon x-small class="ml-1 stat-act" :loading="busyCode === item.code" :disabled="!!busyCode"
                title="Buka semula (kembali Aktif)" @click="openKuiri(item.code)">
                <v-icon x-small color="green darken-1">mdi-lock-open-variant</v-icon>
              </v-btn>
            </template>
            <!-- Aktif: can be closed. -->
            <template v-else-if="kuiriStatus(item) === 'active'">
              <v-chip color="green darken-1" dark x-small label>Aktif</v-chip>
              <v-btn icon x-small class="ml-1 stat-act" :loading="busyCode === item.code" :disabled="!!busyCode"
                title="Tutup kuiri ini (Semak Semula akan mengabaikannya)" @click="closeKuiri(item.code)">
                <v-icon x-small color="red darken-1">mdi-lock</v-icon>
              </v-btn>
            </template>
            <v-tooltip v-else-if="kuiriStatus(item) === 'registered'" bottom max-width="420">
              <template v-slot:activator="{ on, attrs }">
                <v-chip v-bind="attrs" v-on="on" color="orange darken-2" dark x-small label>Ulangan</v-chip>
              </template>
              <span>{{ item.skipReason }}</span>
            </v-tooltip>
            <v-tooltip v-else-if="item.skipReason" bottom max-width="420">
              <template v-slot:activator="{ on, attrs }">
                <v-chip v-bind="attrs" v-on="on" color="grey lighten-1" dark x-small label>Nyahaktif</v-chip>
              </template>
              <span>{{ item.skipReason }}</span>
            </v-tooltip>
            <v-chip v-else color="grey lighten-1" x-small label>Nyahaktif</v-chip>
          </div>
        </template>
      </v-data-table>

    </v-card>
  </v-container>
</template>

<script>
import axios from "../../plugins/axios";

export default {
  name: "ModulValidasiRujukan",
  data() {
    return {
      loading: false,

      // shared
      search: "",
      statusFilter: null,

      // Buku Panduan
      kuiri: [],
      families: {},
      familyFilter: null,
      jenisFilter: null,

      // Same three words as the chips in the Status Enjin column and the legend
      // above the table — one vocabulary, so the filter reads as picking a chip.
      panduanStatusOptions: [
        { text: "Aktif", value: "active" },
        { text: "Tutup", value: "closed" },
        { text: "Ulangan", value: "registered" },
        { text: "Nyahaktif", value: "none" },
      ],
      // Legend for the Status Enjin column. Colours match the chips exactly, so
      // it doubles as the colour key.
      statusLegend: [
        {
          label: "Aktif", color: "green darken-1", dark: true,
          text: "Enjin menilai kuiri ini pada setiap HID. Kesalahan yang ditemui akan muncul dalam Senarai HID.",
        },
        {
          label: "Tutup", color: "red darken-1", dark: true,
          text: "Kuiri ditutup secara manual - Semak Semula mengabaikannya sepenuhnya. Hanya kuiri Aktif boleh ditutup, dan kuiri Tutup boleh dibuka semula kepada Aktif. Jalankan Semak Semula selepas menukar status supaya keputusan tersimpan dikemas kini.",
        },
        {
          label: "Ulangan", color: "orange darken-2", dark: true,
          text: "Kuiri didaftar tetapi tidak dinilai kerana semakannya sudah diliputi oleh kod Panduan yang lain. Kesalahannya tetap tertangkap - melalui kod tersebut. Tuding pada lencana dalam jadual untuk melihat sebabnya.",
        },
        {
          label: "Nyahaktif", color: "grey lighten-1", dark: false,
          text: "Kuiri belum dilaksanakan dalam enjin - lazimnya kerana lajur sumbernya tiada dalam pangkalan data atau semantiknya belum disahkan. Ia tidak disemak langsung.",
        },
      ],
      // Only Kod and Keluarga Kuiri sort. The rest are free text (Semakan,
      // Tindakan) or multi-valued (Pembolehubah is an array), where an
      // alphabetical order says nothing useful — the filters above are how you
      // narrow those.
      panduanHeaders: [
        { text: "Kod", value: "code", width: 90 },
        // 130, not 110: "Jenis Kuiri" plus the sort arrow needs ~125px, and
        // the column is nowrap (see the style block) so a narrower hint would
        // just be overridden by the browser.
        { text: "Jenis Kuiri", value: "family", width: 130 },
        { text: "Jenis Rekod", value: "jenisRekod", width: 110, sortable: false },
        { text: "Pembolehubah", value: "vars", sortable: false },
        { text: "Semakan (Panduan)", value: "semakan", sortable: false },
        // Sits immediately after Semakan so the Panduan's wording and what the
        // engine actually does are read side by side - that pairing is the whole
        // point of the column.
        { text: "Logik Enjin", value: "logik", sortable: false },
        { text: "Tindakan", value: "tindakan", sortable: false },
        // Wider than before — the cell now also carries a Tutup/Buka action button.
        { text: "Status Enjin", value: "status", width: 150, sortable: false },
      ],
      // Which code has a Tutup/Buka request in flight (disables its button).
      busyCode: null,
      snackbar: { show: false, message: "", color: "success" },
    };
  },
  computed: {
    counts() {
      return {
          total: this.kuiri.length,
          inEngine: this.kuiri.filter((k) => k.inEngine).length,
          active: this.kuiri.filter((k) => k.active && !k.closed).length,
          closed: this.kuiri.filter((k) => k.closed).length,};
    },
    // --- Buku Panduan ---
    familyOptions() {
      return Object.keys(this.families).map((k) => ({ value: k, text: `${k} - ${this.families[k]}` }));
    },
    jenisOptions() {
      return [...new Set(this.kuiri.map((k) => k.jenisRekod).filter(Boolean))].sort();
    },
    filteredKuiri() {
      return this.kuiri.filter((k) => {
        if (this.familyFilter && k.family !== this.familyFilter) return false;
        if (this.jenisFilter && k.jenisRekod !== this.jenisFilter) return false;
        // Same classifier as the chip, so filtering by Nyahaktif shows exactly the
        // grey chips (incl. disabled codes like SJ-135), not just !inEngine ones.
        if (this.statusFilter && this.kuiriStatus(k) !== this.statusFilter) return false;
        return true;
      });
    },
  },
  mounted() { this.load(); },
  methods: {
    resetFilters() {
      this.familyFilter = null;
      this.jenisFilter = null;
      this.statusFilter = null;
      this.search = '';
    },
    async load() {
      this.loading = true;
      try {
        const { data } = await axios.get(`/modul-validasi/kuiri`);
          this.kuiri = data.data.kuiri;
          this.families = data.data.meta.families || {};
      } catch (e) { /* ignore */ } finally { this.loading = false; }
    },
    muatTurunExcel() {
      const url = (process.env.VUE_APP_API_URL || '').replace(/\/$/, '') + '/modul-validasi/export-rujukan-excel';
      window.open(url, '_blank');
    },
    // Single source of truth for the Status Enjin column, used by both the chip
    // and the filter. Returns the same values as panduanStatusOptions.
    //  active     - engine evaluates it (green Aktif)
    //  registered - registered but skipped as a duplicate of another Panduan code,
    //               reason mentions "diliputi"/"ulangan" (orange Ulangan)
    //  none       - not evaluated: disabled, missing column, or unimplemented
    //               (grey Nyahaktif; reason shown as a tooltip when there is one)
    kuiriStatus(k) {
      // Tutup overlays on top: a closed kuiri is one the engine now ignores even
      // though it is otherwise Aktif. Only Aktif codes can be closed.
      if (k.closed) return "closed";
      if (k.active) return "active";
      const r = String(k.skipReason || "").toLowerCase();
      if (k.inEngine && (r.includes("diliputi") || r.includes("ulangan"))) return "registered";
      return "none";
    },
    sevColor(s) { return s === "K" ? "red darken-1" : s === "A" ? "orange darken-2" : "blue darken-1"; },
    sevLabel(s) { return s === "A" ? "Amaran" : s === "I" ? "Info" : "Kritikal"; },
    notify(message, color = "success") { this.snackbar = { show: true, message, color }; },
    // Close an Aktif kuiri (server verifies it is currently active). Reload so the
    // chip, filter and counts reflect the new state.
    async closeKuiri(code) {
      this.busyCode = code;
      try {
        await axios.post(`/modul-validasi/tutup`, { code });
        this.notify(`Kuiri ${code} telah ditutup. Jalankan Semak Semula untuk mengemas kini keputusan.`);
        await this.load();
      } catch (e) {
        this.notify((e.response && e.response.data && e.response.data.message) || `Gagal menutup ${code}.`, "error");
      } finally { this.busyCode = null; }
    },
    // Reopen a closed kuiri back to Aktif.
    async openKuiri(code) {
      this.busyCode = code;
      try {
        await axios.delete(`/modul-validasi/tutup`, { data: { code } });
        this.notify(`Kuiri ${code} dibuka semula (Aktif). Jalankan Semak Semula untuk mengemas kini keputusan.`);
        await this.load();
      } catch (e) {
        this.notify((e.response && e.response.data && e.response.data.message) || `Gagal membuka semula ${code}.`, "error");
      } finally { this.busyCode = null; }
    },
  },
};
</script>

<style scoped>
/* Title + blurb, stacked at the left end of the card title. */
.header-left {
  display: flex;
  flex-direction: column;
  min-width: 0;      /* lets the blurb wrap instead of forcing the row wider */
  flex: 1 1 420px;
}
.header-left__title { display: flex; align-items: center; }
/* The blurb is body text inside a text-h6 container, so every heading property
   has to be reset - size, weight, tracking, and Vuetify's break-all. */
.header-left__sub {
  margin-top: 3px;
  max-width: 640px;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.45;
  letter-spacing: normal;
  color: rgba(0, 0, 0, 0.6);
  word-break: normal;
  overflow-wrap: anywhere;
}

/* Counts + legend, stacked at the right end of the card title. */
.header-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 0;
}
.header-right__chips { display: flex; flex-wrap: wrap; justify-content: flex-end; }

/* Status Enjin legend. The chip column is fixed-width so the three
   descriptions start on the same left edge and read as a list.
   `word-break: normal` is REQUIRED: Vuetify sets `word-break: break-all` on
   .v-card__title, which would chop this prose mid-word. `text-align: left`
   likewise - the parent right-aligns, and ragged-right is what makes the three
   entries scan as a list. */
.status-legend {
  margin: 4px 4px 0;
  padding: 8px 12px;
  max-width: 560px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fafafa;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: normal;
  color: #546e7a;
  word-break: normal;
  overflow-wrap: anywhere;
  text-align: left;
}
.status-legend__head {
  margin-bottom: 6px;
  color: #37474f;
}
.status-legend__row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 3px;
}
.status-legend__chip {
  flex: 0 0 auto;
  width: 74px;
  justify-content: center;
  margin-top: 1px;
}

/* Kod and Keluarga Kuiri each hold a single token — a kuiri code and a family
   tag. Wrapping split the header into "Keluarga" / "Kuiri" on two lines and
   could break a code mid-token, making the first two columns taller than the
   row they label while the wide text columns beside them stayed one line. */
.kriteria-table >>> th:nth-child(1),
.kriteria-table >>> th:nth-child(2),
.kriteria-table >>> td:nth-child(1),
.kriteria-table >>> td:nth-child(2) {
  white-space: nowrap;
}

/* The Kod badge keeps the monospace face so a code still reads as a code even
   inside a coloured chip; the chip colour carries the severity. */
.kod-chip {
  font-family: "Roboto Mono", ui-monospace, "Cascadia Mono", Consolas, monospace;
  font-weight: 600;
  letter-spacing: 0;
}

/* Logik Enjin describes code, so it is set in the mono face and a step down in
   size: it reads as the implementation beside the Panduan's prose rather than as
   more prose. Column names (NOAIR, INCS21A) stay legible as tokens that way. */
.logik {
  font-family: "Roboto Mono", ui-monospace, "Cascadia Mono", Consolas, monospace;
  font-size: 11px;
  line-height: 1.45;
  color: #37474f;
  display: block;
}
/* Not-evaluated rules are muted so a scan down the column separates what runs
   from what does not, without having to cross-reference the Status Enjin chip. */
.logik--off {
  color: #90a4ae;
  font-style: italic;
}

/* Status Enjin cell: chip + its Tutup/Buka action button on one line. */
.status-cell-wrap { display: inline-flex; align-items: center; white-space: nowrap; }
.stat-act { margin-top: -1px; }

/* --- TEAL THEME OVERRIDES --- */
.filter-bar {
  background: #f7fcfb;
  border-top: 1px solid #dbeeeb;
  border-bottom: 1px solid #dbeeeb;
}
.modulvalidasi-page .fld__label {
  font-size: var(--fs-3xs, 12px);
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #7aa8a2;
  line-height: 1.4;
  margin-bottom: 5px;
  white-space: nowrap;
}
.modulvalidasi-page .filter-bar .v-input fieldset { border-color: #cfeae7; }
.modulvalidasi-page .filter-bar .v-input--is-focused fieldset {
  border-color: #037769 !important;
  border-width: 1px;
}
.kad__tajuk { border-bottom: 1px solid #dbeeeb; }
.kad__tajuk .v-icon { color: #037769; }
.btn-teal.v-btn {
  background: #037769 !important;
  color: #ffffff !important;
  letter-spacing: normal;
}
.btn-teal.v-btn:hover { background: #026254 !important; }
.btn-teal.v-btn .v-icon { color: #ffffff !important; }
.btn-teal.v-btn--disabled {
  background: #cfd8dc !important;
  color: rgba(0, 0, 0, .38) !important;
}
.btn-teal.v-btn--disabled .v-icon { color: rgba(0, 0, 0, .38) !important; }
.reset-btn.v-btn--outlined {
  border-color: #cfeae7;
  color: #037769;
  letter-spacing: normal;
}
.reset-btn.v-btn--outlined:hover {
  background: #eef8f6;
  border-color: #037769;
}
.reset-btn.v-btn--outlined .v-icon { color: #037769; }
</style>
